import { useState, useMemo } from "react";
import {
    Typography,
    Collapse,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Paper,
    TableContainer,
    Box,
    Stack,
} from "@mui/material";

import {
    findOccurrence,
    getOccurrences,
    getRecurrenceType,
} from "../../helpers/events";
import Icons from "../../helpers/icons";
import { dayjsUTC } from "../../helpers/dates";
import Colours from "../../helpers/colours";

const dayjs = dayjsUTC;

const recurrenceColors = {
    Daily: {
        light: "#DFF5E1", // green light
        dark: "#2E7D32", // green darker
    },
    Weekly: {
        light: "#DDEBFF", // blue light
        dark: "#1565C0", // blue darker
    },
    Monthly: {
        light: "#FFE8D6", // orange light
        dark: "#EF6C00", // orange darker
    },
    Yearly: {
        light: "#F0E5FF", // purple light
        dark: "#6A1B9A", // purple darker
    },
};

const EventOccurrencesViewer = ({
    rrule,
    start,
    end,
    repeatStart,
    collapsible = true,
}) => {
    const [open, setOpen] = useState(false);

    const occurrences = useMemo(() => {
        return getOccurrences(rrule, start, end, repeatStart);
    }, [rrule, start, end, repeatStart]);

    const multiDay = occurrences.length > 0 && occurrences[0].durationDays > 1;

    const TableContent = (
        <TableContainer
            component={Paper}
            sx={{
                borderRadius: 3,
                overflow: "hidden",
                mt: 1,
                mb: 1,
            }}
        >
            <Box
                sx={{
                    maxHeight: 4 * 48,
                    overflowY: "auto",
                    display: "block",
                }}
            >
                <Table size="small" sx={{ borderCollapse: "collapse" }}>
                    <TableHead
                        sx={{
                            backgroundColor: Colours.lightBubble,
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                        }}
                    >
                        <TableRow>
                            <TableCell>#</TableCell>

                            {multiDay ? (
                                <>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>End Date</TableCell>
                                </>
                            ) : (
                                <TableCell>Date</TableCell>
                            )}

                            <TableCell>Duration</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {occurrences.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No occurrences found
                                </TableCell>
                            </TableRow>
                        )}

                        {occurrences.map((o) => {
                            const startDate = o.rawDate;
                            const endDate = startDate.add(
                                o.durationDays - 1,
                                "day",
                            );

                            return (
                                <TableRow
                                    key={o.index}
                                    sx={{
                                        textDecoration: o.isExcluded
                                            ? "line-through"
                                            : "none",
                                        opacity: o.isExcluded ? 0.5 : 1,
                                    }}
                                >
                                    <TableCell>#{o.index}</TableCell>

                                    {multiDay ? (
                                        <>
                                            <TableCell>
                                                {startDate.format(
                                                    "DD MMM YYYY",
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {endDate.format("DD MMM YYYY")}
                                            </TableCell>
                                        </>
                                    ) : (
                                        <TableCell>{o.date}</TableCell>
                                    )}

                                    <TableCell>
                                        {o.durationDays} day
                                        {o.durationDays > 1 ? "s" : ""}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        </TableContainer>
    );

    if (!collapsible) {
        return (
            <div>
                <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        color: Colours.graphDisabled,
                    }}
                >
                    ALL OCCURRENCES
                </Typography>
                {TableContent}
            </div>
        );
    }

    return (
        <div>
            <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    color: Colours.attract,
                }}
            >
                <Box
                    onClick={() => setOpen((o) => !o)}
                    sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        "&:hover": {
                            textDecoration: "underline",
                        },
                    }}
                >
                    View all occurrences ({occurrences.length})
                    <IconButton size="small" sx={{ ml: 1, p: 0 }}>
                        {open ? <Icons.ArrowUp /> : <Icons.ArrowDown />}
                    </IconButton>
                </Box>
            </Typography>

            <Collapse in={open}>{TableContent}</Collapse>
        </div>
    );
};

export default EventOccurrencesViewer;

export const EventOccurrencesViewerPill = ({
    rrule,
    start,
    end,
    repeatStart,
}) => {
    const occ = useMemo(() => {
        return findOccurrence(rrule, start, end, repeatStart);
    }, [rrule, start, end, repeatStart]);

    const recurrenceType = useMemo(() => {
        return getRecurrenceType(rrule);
    }, [rrule]);

    if (!occ) {
        return (
            <Typography color="error">
                Could not match this date range to an occurrence
            </Typography>
        );
    }

    const colors = recurrenceColors[recurrenceType] ?? {
        light: Colours.lightBubble,
        dark: Colours.attract,
    };

    return (
        <Stack
            direction={{ lg: "row", md: "column" }}
            spacing={2}
            alignItems="center"
        >
            {/* Recurrence pill */}

            <Box
                sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: 10,
                    backgroundColor: colors.light,
                    border: `2px solid ${colors.dark}`,
                    color: colors.dark,
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                }}
            >
                {recurrenceType}
            </Box>

            {/* Occurrence counter */}
            <Typography>
                {occ.index} / {occ.totalOccs}
            </Typography>
        </Stack>
    );
};
